import { useDiscovery } from '../context/DiscoveryContext';
import { BusinessSubcategory } from '@/types/discovery';
import { cn } from '@/lib/utils';
import { ArrowRight } from 'lucide-react';
import { trackEvent } from '@/lib/ga4';

interface SubcategoryCardProps {
  subcategory: BusinessSubcategory;
  onClick: () => void;
}

const SubcategoryCard = ({ subcategory, onClick }: SubcategoryCardProps) => {
  const Icon = subcategory.icon;
  const hasCustomIcon = !!subcategory.iconPath;

  return (
    <button
      onClick={onClick}
      className={cn(
        'group relative flex flex-col p-4 sm:p-6 rounded-2xl',
        'bg-white/10 backdrop-blur-sm border border-white/20',
        'hover:bg-white/20 hover:border-white/40 hover:scale-[1.02] transition-all duration-300',
        'text-left w-full h-full'
      )}
    >
      <div className="flex items-start gap-4 mb-4">
        {/* Icon */}
        <div className="p-3 bg-white/10 rounded-xl flex-shrink-0 group-hover:bg-white/20 transition-colors">
          {hasCustomIcon ? (
            <img src={subcategory.iconPath} alt={subcategory.name} className="w-8 h-8 object-contain" />
          ) : (
            <Icon className="w-8 h-8 text-white" />
          )}
        </div>

        {/* Title and arrow */}
        <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
          <h3 className="text-xl sm:text-2xl font-bold text-white">{subcategory.name}</h3>
          <ArrowRight className="w-5 h-5 text-white/40 group-hover:text-white group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
        </div>
      </div>

      {/* Challenges preview */}
      <div className="space-y-2.5 flex-1">
        {subcategory.challenges.slice(0, 3).map((challenge, idx) => (
          <div key={idx} className="flex items-start gap-3 text-base text-white/70">
            <span className="text-purple-400 flex-shrink-0 mt-0.5">✓</span>
            <span>{challenge}</span>
          </div>
        ))}
      </div>
    </button>
  );
};

export const SubcategorySelectStep = () => {
  const { state, selectSubcategory } = useDiscovery();

  if (!state.industry) {
    return null;
  }

  const { industry } = state;
  const hasCustomIcon = !!industry.iconPath;
  const Icon = industry.icon;

  return (
    <div className="text-center">
      {/* Header with industry context */}
      <div className="mb-5 sm:mb-10">
        <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-white/10 backdrop-blur-sm rounded-full mb-6">
          {hasCustomIcon ? (
            <img src={industry.iconPath} alt={industry.name} className="w-6 h-6 object-contain" />
          ) : (
            <Icon className="w-6 h-6 text-white" />
          )}
          <span className="text-base font-medium text-white">{industry.name}</span>
        </div>

        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
          What type of business?
        </h1>
        <p className="text-base sm:text-xl text-white/70">
          Select your specific business type to see tailored genies.
        </p>
      </div>

      {/* Subcategory grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
        {industry.subcategories.map(subcategory => (
          <SubcategoryCard
            key={subcategory.id}
            subcategory={subcategory}
            onClick={() => {
              trackEvent('discover_subcategory_selected', {
                industry: industry.id,
                subcategory: subcategory.id,
              });
              selectSubcategory(subcategory);
            }}
          />
        ))}
      </div>
    </div>
  );
};
